import { useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { useApiService } from "./apiService";
import { UpdateAirframeRequest, CreateAirframeRequest, Airframe } from "../types/airframe";
import { Component } from "../types/component";
import { AttachComponent } from "../types/AttachComponent";
import { DetachComponent, OffWingType } from "../types/DetachComponent";
import { ReplaceComponent } from "../types/ReplaceComponent";

export const useAirframeService = () => {
  const { accounts } = useMsal();
  const apiService = useApiService();

  const getAirframes = useCallback(async () => {
    const response = await apiService.apiCall<Airframe[]>({
      method: "GET",
      endpoint: "/api/airframes",
      requireAuth: true,
    });
    return response;
  }, [apiService]);

  const getAirframe = useCallback(
    async (id: string) => {
      const response = await apiService.apiCall<Airframe>({
        method: "GET",
        endpoint: `/api/airframes/${id}`,
        requireAuth: true,
      });
      return response;
    },
    [apiService]
  );

  const updateAirframe = useCallback(
    async (id: string, request: UpdateAirframeRequest) => {
      try {
        const payload = {
          ...request,
          // Ensure dates are in YYYY-MM-DD format
          dateManufactured:
            request.dateManufactured instanceof Date
              ? request.dateManufactured.toISOString().split("T")[0]
              : typeof request.dateManufactured === 'string'
              ? request.dateManufactured.split('T')[0] 
              : request.dateManufactured,
          lastHMVDate:
            request.lastHMVDate instanceof Date
              ? request.lastHMVDate.toISOString().split("T")[0]
              : typeof request.lastHMVDate === 'string'
              ? request.lastHMVDate.split('T')[0] 
              : request.lastHMVDate,
        };

        const response = await apiService.apiCall<any>({
          method: "PUT",
          endpoint: `/api/airframes/${id}`,
          payload,
          requireAuth: true,
        });
        return response?.success !== false;
      } catch (error) {
        if (
          error instanceof Error &&
          (error.message.includes("JSON") || error.message.includes("Unexpected end"))
        ) {
          return true;
        }
        throw error;
      }
    },
    [apiService]
  );

  const createAirframe = useCallback(
    async (request: CreateAirframeRequest) => {
      try {
        const payload = {
          ...request,
          // Normalize dates to YYYY-MM-DD format
          dateManufactured:
            request.dateManufactured instanceof Date
              ? request.dateManufactured.toISOString().split("T")[0]
              : typeof request.dateManufactured === 'string'
              ? request.dateManufactured.split('T')[0]
              : request.dateManufactured,
        };

        const response = await apiService.apiCall<Airframe>({
          method: "POST",
          endpoint: "/api/airframes",
          payload,
          requireAuth: true,
        });
        
        return response;
      } catch (error) {
        throw error;
      }
    },
    [apiService]
  );

  const deleteAirframe = useCallback(async (id: string) => {
  try {
    const response = await apiService.apiCall<any>({
      method: "DELETE",
      endpoint: `/api/airframes/${id}`,
      requireAuth: true,
    });

    return response?.success !== false;
  } catch (error) {
    if (
      error instanceof Error &&
      (error.message.includes("JSON") || error.message.includes("Unexpected end"))
    ) {
      return true;
    }
    throw error;
  }
}, [apiService]);

const attachComponentToAirframe = useCallback(
  async (
    airframeId: string,
    componentId: string,
    slotId: string
  ): Promise<Component | null> => {
    try {
      const createdBy =
        accounts?.[0]?.name?.trim() ||
        accounts?.[0]?.username?.trim() ||
        "system";

      const payload: AttachComponent = {
        airframeId,
        componentId,
        slotId,
        createdBy,
      };

      const response = await apiService.apiCall<Component>({
        method: "POST",
        endpoint: `/api/airframes/attachcomponent`,
        payload,
        requireAuth: true,
      });

      // Return the attached component with its details (attachedToAirframe, attachmentLocation)
      return response?.data || null;
    } catch (error) {
      if (
        error instanceof Error &&
        (error.message.includes("JSON") || error.message.includes("Unexpected end"))
      ) {
        return null;
      }
      throw error;
    }
  },
  [apiService, accounts]
);

const detachComponentFromAirframe = useCallback(
  async (
    airframeId: string,
    componentId: string,
    slotId: string,
    offWingType?: OffWingType,
    shopVisitReasonId?: string | null,
    shopVisitReasonLabel?: string | null
  ): Promise<Component | null> => {
    try {
      const editedBy =
        accounts?.[0]?.name?.trim() ||
        accounts?.[0]?.username?.trim() ||
        "system";

      const payload: DetachComponent = {
        airframeId,
        componentId,
        slotId,
        editedBy,
        ...(offWingType && { offWingType }),
        ...(shopVisitReasonId && { shopVisitReasonId }),
        ...(shopVisitReasonLabel && { shopVisitReasonLabel }),
      };
      const response = await apiService.apiCall<Component>({
        method: "POST",
        endpoint: `/api/airframes/detachcomponent`,
        payload,
        requireAuth: true,
      });

      // Return the detach component with its details (attachedToAirframe, attachmentLocation)
      return response?.data || null;
    } catch (error) {
      if (
        error instanceof Error &&
        (error.message.includes("JSON") || error.message.includes("Unexpected end"))
      ) {
        return null;
      }
      throw error;
    }
  },
  [apiService, accounts]
);

const replaceComponentInAirframe = useCallback(
  async (airframeId: string, oldComponentId: string, newComponentId: string) => {
    try {

      const editedBy =
        accounts?.[0]?.name?.trim() ||
        accounts?.[0]?.username?.trim() ||
        "system";

      const payload: ReplaceComponent = {
        airframeId: airframeId,
        currentComponentId:oldComponentId,
        newComponentId:newComponentId,
        editedBy:editedBy
      };
      const response = await apiService.apiCall<Component>({
        method: "POST",
        endpoint: `/api/airframes/replacecomponent`,
        payload,
        requireAuth: true,
      });

      return response?.data || null;      
    } catch (error) {
      if (
        error instanceof Error &&
        (error.message.includes("JSON") || error.message.includes("Unexpected end"))
      ) {
        return { success: true, data: null };
      }
      throw error;
    }
  },
  [apiService, accounts]
);

  return {
    getAirframes,
    getAirframe,
    createAirframe,
    updateAirframe,
    deleteAirframe,
    attachComponentToAirframe,
    detachComponentFromAirframe,
    replaceComponentInAirframe  
  };
};